import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";

export function ExplainBudgetButton({ order, onSaved }) {
  const items = order.budget?.items || [];
  const [open, setOpen] = useState(false);
  const [texts, setTexts] = useState([]);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) { return; }
    setTexts(items.map((i) => i.explanation || ""));
    api.get("/ai/usage").then(({ data }) => setUsage(data)).catch(() => setUsage(null));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const atLimit = usage && usage.used >= usage.limit;
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/budget/explain`, { items: items.map((i) => ({ description: i.description, quantity: Number(i.quantity) || 1 })) });
      setTexts(data.explanations); setUsage((u) => ({ ...u, used: data.used, limit: data.limit }));
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/orders/${order.id}/budget/explanations`, { explanations: texts }); const { data } = await api.get(`/orders/${order.id}`); onSaved?.(data); toast.success("Explicações salvas: o cliente já vê abaixo de cada item"); setOpen(false); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  if (!items.length) return null;
  return (
    <>
      <Button data-testid="budget-explain-btn" type="button" variant="outline" onClick={() => setOpen(true)} className="bg-zinc-900 border-zinc-800 text-zinc-200"><Sparkles className="h-4 w-4 mr-1 text-amber-300" /> Explicar para o cliente</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="explain-dialog" className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-300" /> Explicar para o cliente</DialogTitle>
            <DialogDescription className="text-zinc-400">A IA escreve um texto curto por item. Revise e edite antes de salvar.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <span data-testid="ai-usage-counter" className={`text-xs ${atLimit ? "text-red-400" : "text-zinc-400"}`}>{usage ? `${usage.used}/${usage.limit} este mês` : "…"}</span>
            <Button data-testid="explain-generate-btn" size="sm" onClick={generate} disabled={busy || !usage || atLimit || usage.needs_shop} className="bg-amber-brand text-black font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : texts.some(Boolean) ? "Gerar de novo" : "Gerar com IA"}</Button>
          </div>
          {atLimit && <div data-testid="ai-limit-warning" className="text-xs text-red-300 rounded-md border border-red-900 bg-red-950/40 p-2">Limite mensal de explicações atingido. Zera no dia 1º; você ainda pode escrever à mão.</div>}
          {usage?.needs_shop && <div className="text-xs text-amber-300">Escolha uma unidade no seletor do topo.</div>}
          <div className="space-y-3">
            {items.map((it, i) => (
              <div key={i}>
                <div className="text-sm font-semibold text-zinc-200">{it.description}</div>
                <Textarea data-testid={`explain-text-${i}`} value={texts[i] || ""} onChange={(e) => setTexts((p) => p.map((t, j) => (j === i ? e.target.value : t)))} placeholder="Explicação simples (opcional)" className="mt-1 bg-zinc-900 border-zinc-800 text-sm min-h-[60px]" />
              </div>
            ))}
          </div>
          <Button data-testid="explain-save-btn" onClick={save} disabled={saving} className="bg-green-brand text-white font-bold uppercase">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar para o cliente"}</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
